import React, { FC, useMemo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import Ghost from '@gravity-ui/icons/Ghost';
import Shield from '@gravity-ui/icons/Shield';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { AdminSettings } from 'client/blocks/AdminSettings/AdminSettings';
import { AliceAppAccountLinking } from 'client/blocks/AliceAppAccountLinking/AliceAppAccountLinking';
import { AliceAppDonationPage } from 'client/blocks/AliceAppDonationPage/AliceAppDonationPage';
import { AliceAppEditorPage } from 'client/blocks/AliceAppEditorPage/AliceAppEditorPage';
import { AliceAppMonitoringPage } from 'client/blocks/AliceAppMonitoring/AliceAppMonitoringPage';
import { AliceAppPromotionPage } from 'client/blocks/AliceAppPromotionPage/AliceAppPromotionPage';
import { AliceAppRatingPage } from 'client/blocks/AliceAppRatingPage';
import { AliceAppTestPage } from 'client/blocks/AliceAppTestPage/AliceAppTestPage';
import { IconSmall } from 'client/blocks/AppIcons/AppIcons';
import { DescribeAppInIcons } from 'client/blocks/DescribeAppInIcons/DescribeAppInIcons';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { FeatureFlags } from 'client/blocks/FeatureFlags/FeatureFlags';
import { getMapChannelToAppCard } from 'client/blocks/icons/mapChannelToAppCard';
import { SettingPageLayout } from 'client/blocks/Layout/Layout';
import { SideMenu } from 'client/blocks/Menu/Menu';
import { IMenuMobileItem, MenuMobile } from 'client/blocks/MenuMobile/MenuMobile';
import { NdaLink } from 'client/blocks/NdaLink/NdaLink';
import { AliceAppOperationEvents } from 'client/blocks/OperationEvents/AliceAppOperationEvents';
import { Resources } from 'client/blocks/Resources/Resources';
import { AliceSkillsAppSettingsPage } from 'client/blocks/SettingsPage/AliceSkillAppSettingsPage';
import { SharingPage } from 'client/blocks/Sharing/SharingPage';
import { SmartHomeTestPage } from 'client/blocks/SmartHomeTestPage/SmartHomeTestPage';
import { DeploymentStatusMobile } from 'client/blocks/statuses/DeploymentStatus/DeploymentStatus';
import { SkillDeploymentStatus } from 'client/blocks/statuses/DeploymentStatus/SkillDeploymentStatus';
import { OperationStatus } from 'client/blocks/statuses/OperationStatus/OperationStatus';
import { SettingsPageContext } from 'client/context/settingsPageContext';
import { useSkillMenu } from 'client/hooks/useSkillMenu';
import { moment } from 'client/lib/moment';
import { routes } from 'client/lib/routes';
import { isInDevelopment } from 'client/model/draft';
import { AliceSkill, Channel, SmartHome } from 'client/model/skill';
import { UserAgreement } from 'client/model/userAgreement';
import { getCanEditApp, getIsGlobalViewer, getIsYangoRequest } from 'client/utils';
import * as keyset from 'i18n/AppsIndex';
import * as footerKeyset from 'i18n/Footer';

import styles from './AppPage.module.scss';
import { getAppHeader } from './utils';

interface Props {
    skill: AliceSkill | SmartHome;
    isDraft: boolean;
    userAgreements: UserAgreement[];
    userAgreementsDraft: UserAgreement[];
}

const t = i18n(keyset);
const tFooter = i18n(footerKeyset);

export const AliceSkillAppPage: FC<Props> = ({ skill, isDraft, userAgreements, userAgreementsDraft }) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { menuItems, displaySkillMenu: display } = useSkillMenu({ skill, isDraft });

    const mapChannelToAppCard = useMemo(() => getMapChannelToAppCard(), []);

    let copyright = t('yandex-copyright', { year: moment().year() });
    let externalAsideLinks: IMenuMobileItem[] = [
        {
            value: routes.documentation.aliceSkills.feedback,
            content: t('feedback'),
        },
        {
            value:
                skill.channel === Channel.SmartHome
                    ? routes.documentation.smartHome.about
                    : routes.documentation.aliceSkills.about,
            content: t('docs'),
        },
    ];

    if (getIsYangoRequest()) {
        copyright = tFooter('copyrightYango', { year: moment().year() });
        externalAsideLinks = [
            {
                value: routes.documentationYango.feedback,
                content: t('feedback'),
            },
            {
                value: routes.documentationYango.index,
                content: t('docs'),
            },
        ];
    }

    const disabled = !getCanEditApp(skill);

    return (
        <ErrorBoundary withHomepageLink>
            <SettingsPageContext.Provider
                value={{
                    isDraft,
                    isEditable: isDraft && isInDevelopment(skill),
                    hasUnsavedChanges,
                    setHasUnsavedChanges,
                }}
            >
                <SettingPageLayout>
                    <section className={styles.container}>
                        <div className={styles.leftColumn}>
                            <div>
                                <div className={styles.menuHeader}>
                                    <div className={styles.menuHeader__installableAppNameBlock}>
                                        <IconSmall
                                            imageUrl={skill.onAir ? skill.logo2?.url : skill.draft?.logo2?.url}
                                        />
                                        <h4 className={styles.menuHeader__header}>{getAppHeader(skill)}</h4>
                                    </div>
                                    <div className={styles.iconStatusBlock}>
                                        {__appConfig.user.isAdmin && <Shield className={styles.adminIcon} />}
                                        {getIsGlobalViewer() && <Ghost className={styles.viewerIcon} />}
                                        {mapChannelToAppCard?.[skill?.channel]?.icon}
                                    </div>
                                </div>
                                <SkillDeploymentStatus skill={skill} />
                                <SideMenu menuItems={menuItems} />
                            </div>
                            <div>
                                {externalAsideLinks.map((asideLink) => (
                                    <a
                                        key={asideLink.value}
                                        href={asideLink.value}
                                        className={clx(styles.link, styles.footerElement)}
                                    >
                                        {asideLink.content}
                                    </a>
                                ))}

                                <div className={styles.footerElement}>
                                    <NdaLink />
                                    {copyright}
                                </div>
                            </div>
                        </div>
                        <div className={styles.installableAppPageContent}>
                            <header className={styles.header__container}>
                                <div className={styles.header__statusContainer}>
                                    <div className={styles.header__commonStatusContainer}>
                                        <DescribeAppInIcons app={skill} />
                                        <div className={styles.header__operationStatusDesktop}>
                                            <OperationStatus isDraft={isDraft} />
                                        </div>
                                    </div>
                                    <DeploymentStatusMobile app={skill} />
                                </div>
                            </header>
                            <MenuMobile menuItems={menuItems} externalAsideLinks={externalAsideLinks}>
                                <Routes>
                                    <Route
                                        path="settings/*"
                                        element={
                                            <AliceSkillsAppSettingsPage
                                                skill={skill}
                                                isDraft={isDraft}
                                                userAgreements={userAgreements}
                                                userAgreementsDraft={userAgreementsDraft}
                                                disabled={disabled}
                                            />
                                        }
                                    />
                                    <Route path="sharing" element={<SharingPage aliceApp={skill} />} />
                                    {display.test && skill.channel === Channel.AliceSkill && (
                                        <Route
                                            path="test"
                                            element={<AliceAppTestPage skill={skill} isDraft={isDraft} />}
                                        />
                                    )}
                                    {display.smartHomeTest && (
                                        <Route
                                            path="test"
                                            element={<SmartHomeTestPage skill={skill} isDraft={isDraft} />}
                                        />
                                    )}
                                    {display.promotion && (
                                        <Route path="promotion" element={<AliceAppPromotionPage skill={skill} />} />
                                    )}
                                    {display.rating && <Route path="rating" element={<AliceAppRatingPage />} />}
                                    {display.resources && skill.channel === Channel.AliceSkill && (
                                        <Route
                                            path="resources/*"
                                            element={<Resources skill={skill} disabled={disabled} />}
                                        />
                                    )}
                                    {display.monitoring && (
                                        <Route path="monitoring" element={<AliceAppMonitoringPage skill={skill} />} />
                                    )}
                                    {display.donation && (
                                        <Route path="donation" element={<AliceAppDonationPage disabled={disabled} />} />
                                    )}
                                    {display.sharing && (
                                        <Route path="sharing" element={<SharingPage aliceApp={skill} />} />
                                    )}
                                    {display.editorFields && (
                                        <Route path="editing" element={<AliceAppEditorPage disabled={disabled} />} />
                                    )}
                                    {display.featureFlags && <Route path="feature-flags" element={<FeatureFlags />} />}
                                    {display.adminSettings && (
                                        <Route path="admin-settings" element={<AdminSettings disabled={disabled} />} />
                                    )}
                                    {display.operations && (
                                        <Route path="operations" element={<AliceAppOperationEvents skill={skill} />} />
                                    )}
                                    {display.authorization && (
                                        <Route
                                            path="authorization"
                                            element={<AliceAppAccountLinking skill={skill} disabled={disabled} />}
                                        />
                                    )}
                                    <Route path="*" element={<Navigate to="../settings" replace />} />
                                </Routes>
                            </MenuMobile>
                            <div className={styles.footerMobile}>
                                <NdaLink />
                                {copyright}
                            </div>
                        </div>
                    </section>
                </SettingPageLayout>
            </SettingsPageContext.Provider>
        </ErrorBoundary>
    );
};
